import { DOCUMENT, Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { TransitionService } from '@uirouter/core';

export type AnalyticsProperties = Record<string, unknown>;

interface AnalyticsWindow extends Window {
  dataLayer: Array<Record<string, unknown>>;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  location?: string;
  private readonly document = inject(DOCUMENT);
  private readonly locationService = inject(Location);
  private readonly transitionService = inject(TransitionService);
  private loaded = false;

  constructor() {
    this.analyticsWindow.dataLayer = this.analyticsWindow.dataLayer || [];
  }

  track(
    eventName: string,
    properties: AnalyticsProperties | string = {},
  ): void {
    const eventProperties: AnalyticsProperties =
      typeof properties === 'string'
        ? { stringProperty: properties }
        : properties || {};

    eventProperties.category = 'apps';
    this.analyticsWindow.dataLayer.push({
      event: 'analytics.track',
      eventName,
      analytics: {
        event: {
          properties: eventProperties,
        },
      },
    });
  }

  identify(userId: string, properties: AnalyticsProperties): void {
    this.analyticsWindow.dataLayer.push({
      event: 'analytics.identify',
      userId,
      analytics: {
        user: {
          properties,
        },
      },
    });
  }

  page(properties: AnalyticsProperties = {}): void {
    properties.category = 'apps';
    this.analyticsWindow.dataLayer.push({
      event: 'analytics.page',
      eventName: 'page viewed',
      analytics: {
        event: {
          properties,
        },
      },
    });
  }

  load(gtmContainerId?: string, gtmAuth?: string, gtmEnv?: string): void {
    if (!gtmContainerId || this.loaded) {
      return;
    }

    this.analyticsWindow.dataLayer.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    });

    const script = this.document.createElement('script');
    const firstScript = this.document.getElementsByTagName('script')[0];
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmContainerId}`;

    if (gtmAuth && gtmEnv) {
      script.src +=
        `&gtm_auth=${gtmAuth}` +
        `&gtm_preview=${gtmEnv}` +
        '&gtm_cookies_win=x';
    }

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      this.document.head.appendChild(script);
    }

    this.loaded = true;
    this.trackPageviews();
  }

  private trackPageviews(): void {
    this.transitionService.onSuccess({}, () => {
      const path = this.locationService.path();

      if (this.location !== path) {
        this.location = path;
        const properties: AnalyticsProperties = {
          url: path,
          path,
        };

        if (new URLSearchParams(window.location.search).has('nooverride')) {
          properties.referrer = '';
        }

        this.page(properties);
      }
    });
  }

  private get analyticsWindow(): AnalyticsWindow {
    return window as unknown as AnalyticsWindow;
  }
}
